import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { FormattedPlural } from 'react-intl';
import { Table as VirtualTable, Column, InfiniteLoader, WindowScroller } from 'react-virtualized';
import Button from '@ontruck/ontruck-react-components/lib/Button';
import styles from './style.module.scss';
import LoadingSection from 'components/LoadingSection';

import ScrollElementContext from 'containers/App/context';

import VehicleListHeader from 'modules/vehicle-section/VehicleList/List/Header';

import 'react-virtualized/styles.css';

import {
  PlateNumber,
  MaxPallets,
  VehicleType,
  MaxWeight,
  MaxHeight,
  MaxWidth,
  LiftGate,
  LateralLoad,
  TopLoad,
  CarrierName,
  Phone,
  JobsDone,
  Scoring,
  LastJob,
  Zipcode,
  InactiveTag,
  CallStatus,
  EngineType,
  EuroCategory,
} from './Columns';

const VehicleList = ({
  vehicles,
  intl,
  showSummary = true,
  showCarrier = true,
  showActions = true,
  showJobsDone = true,
  showPostalCode = true,
  showInactiveTag,
  totalVehicles,
  handleRowClick,
  handleResetFilters,
  showCallStatusModelHandler = () => {
    /*noop*/
  },
  handleCarrierClick = () => {
    /*noop*/
  },
  handleLoadMoreVehicles = () => {
    /*noop*/
  },
  handleGetRow,
  handleIsRowLoaded,
  handleRowRendering,
  fetching,
}) => {
  const scrollElement = useContext(ScrollElementContext);

  const t = intl.messages.components.VehicleList;

  if (fetching || !scrollElement) {
    return <LoadingSection />;
  }

  return (
    <div>
      <div className={styles.summary}>
        {showSummary && (
          <>
            {!totalVehicles && <div>{t.no_vehicles}</div>}
            {totalVehicles && (
              <div>
                {totalVehicles}{' '}
                <FormattedPlural value={vehicles.length} one={t.vehicle} other={t.vehicles} /> -{' '}
                <Button link className={styles.clearButton} onClick={handleResetFilters}>
                  {t.remove_filters}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      {vehicles.length > 0 && (
        <>
          <VehicleListHeader />
          <WindowScroller scrollElement={scrollElement.current}>
            {({ height, isScrolling, onChildScroll, scrollTop }) => {
              return (
                <InfiniteLoader
                  isRowLoaded={handleIsRowLoaded}
                  loadMoreRows={handleLoadMoreVehicles}
                  rowCount={totalVehicles}
                  threshold={30}
                >
                  {({ onRowsRendered }) => {
                    return (
                      <VirtualTable
                        autoHeight
                        disableHeader
                        height={height}
                        rowHeight={70}
                        rowCount={vehicles.length}
                        rowGetter={handleGetRow}
                        width={1400}
                        rowClassName={() => styles.row}
                        onRowClick={handleRowClick}
                        onRowsRendered={onRowsRendered}
                        scrollTop={scrollTop}
                        rowRenderer={handleRowRendering}
                        overscanRowCount={10}
                        isScrolling={isScrolling}
                        onScroll={onChildScroll}
                      >
                        <Column dataKey="plate_number" width={64} cellRenderer={PlateNumber} />
                        <Column dataKey="vehicle" width={80} cellRenderer={VehicleType} />
                        <Column dataKey="max_pallets" width={56} cellRenderer={MaxPallets} />
                        <Column dataKey="max_weight" width={80} cellRenderer={MaxWeight} />
                        <Column dataKey="container_height" width={56} cellRenderer={MaxHeight} />
                        <Column dataKey="container_width" width={56} cellRenderer={MaxWidth} />

                        <Column dataKey="has_liftgate" width={40} cellRenderer={LiftGate} />
                        <Column
                          dataKey="has_lateral_loading"
                          width={40}
                          cellRenderer={LateralLoad}
                        />
                        <Column dataKey="has_superior_loading" width={40} cellRenderer={TopLoad} />
                        {showCarrier && (
                          <Column
                            dataKey="journeys"
                            width={195}
                            cellRenderer={CarrierName(handleCarrierClick)}
                          />
                        )}
                        {showPostalCode && (
                          <Column dataKey="zipcode" width={56} cellRenderer={Zipcode} />
                        )}
                        <Column dataKey="carrier" width={80} cellRenderer={LastJob} />
                        {showJobsDone && (
                          <Column dataKey="carrier" width={40} cellRenderer={JobsDone} />
                        )}
                        <Column
                          dataKey="carrier"
                          width={59}
                          cellRenderer={Scoring}
                          style={{ marginRight: '24px' }}
                        />
                        <Column dataKey="engine_type" width={60} cellRenderer={EngineType} />
                        <Column dataKey="euro_category" width={55} cellRenderer={EuroCategory} />
                        {showInactiveTag && (
                          <Column dataKey="is_active" width={223} cellRenderer={InactiveTag} />
                        )}
                        <Column
                          dataKey="carrier"
                          width={90}
                          cellRenderer={CallStatus(showCallStatusModelHandler)}
                        />
                        {showActions && (
                          <Column dataKey="actions" width={24} cellRenderer={Phone} />
                        )}
                      </VirtualTable>
                    );
                  }}
                </InfiniteLoader>
              );
            }}
          </WindowScroller>
        </>
      )}
    </div>
  );
};

VehicleList.propTypes = {
  vehicles: PropTypes.array,
  intl: PropTypes.any,
  handleResetFilters: PropTypes.func,
  showSummary: PropTypes.bool,
  showActions: PropTypes.bool,
  showStatus: PropTypes.bool,
  handleRowClick: PropTypes.func,
  showCarrier: PropTypes.bool,
  scrollElement: PropTypes.any,
  push: PropTypes.func,
};

export default VehicleList;
